import { type ReactNode, useMemo, useState } from 'react';

import AudienceDialog from '@/components/AudienceDialog';
import CalendarSyncDialog from '@/components/CalendarSyncDialog';
import ConfirmDialog, { type ConfirmOptions } from '@/components/ConfirmDialog';
import DeleteEventDialog from '@/components/DeleteEventDialog';
import EventFormDialog, { draftFromEvent, emptyDraft } from '@/components/EventFormDialog';
import EventSheet from '@/components/EventSheet';
import { InstallDialog } from '@/components/InstallApp';
import MemberPicker from '@/components/MemberPicker';
import { PrivacyCenterDialog } from '@/components/Privacy';
import ShareDialog from '@/components/ShareDialog';
import type { ShareScope } from '@escala/domain/share';
import { formatDayMonth } from '@escala/domain/dates';
import { duplicateDraft } from '@escala/domain/eventSeries';
import type { EventDraft, EventScope } from '@escala/domain/transitions';
import type { ScheduleEvent } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

import { type EventSheetTab, type OverlayApi, OverlayContext, type PickerRequest } from './context';

export default function OverlayProvider({ children }: { children: ReactNode }) {
  const { today, state, actions } = useStore();
  const events = state.events;
  const ministries = state.ministries;
  const [opened, setOpened] = useState<{ eventId: string; tab: EventSheetTab } | null>(null);
  const [audience, setAudience] = useState<{ eventId: string; pick: boolean } | null>(null);
  const [picker, setPicker] = useState<PickerRequest | null>(null);
  const [share, setShare] = useState<{ eventIds: string[]; scope: ShareScope } | null>(null);
  const [draft, setDraft] = useState<{
    initial: EventDraft;
    occurrenceOf?: string;
    copiedFrom?: ScheduleEvent;
  } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null);
  const [deleting, setDeleting] = useState<{ event: ScheduleEvent; initial: EventScope } | null>(null);
  const [calendar, setCalendar] = useState(false);
  const [install, setInstall] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  const api = useMemo<OverlayApi>(
    () => ({
      openEvent: (eventId, tab = 'escala') => setOpened({ eventId, tab }),
      openAudience: (eventId, options = {}) => setAudience({ eventId, pick: !!options.pick }),
      openPicker: setPicker,
      openShare: (eventIds, scope = {}) => setShare({ eventIds, scope }),
      openCalendar: () => setCalendar(true),
      openInstall: () => setInstall(true),
      openPrivacy: () => setPrivacy(true),
      newEvent: (date) => setDraft({ initial: emptyDraft(date ?? today) }),
      editEvent: (event, scope = 'one') => setDraft({ initial: draftFromEvent(event, scope) }),
      newOccurrence: (seriesId) => setDraft({ initial: emptyDraft(today), occurrenceOf: seriesId }),
      duplicateEvent: (event) =>
        setDraft({
          initial: duplicateDraft(emptyDraft(today), event, ministries),
          copiedFrom: event,
        }),
      // Uma data só: a confirmação simples. Com mais datas: só esta, esta e as próximas, ou todas.
      deleteEvent: (event, initial = 'one') => {
        if (events.filter((e) => e.seriesId === event.seriesId).length > 1) {
          setDeleting({ event, initial });

          return;
        }

        setConfirm({
          title: 'Excluir evento',
          text: `${event.title} · ${formatDayMonth(event.date)} e a escala deste evento serão excluídos.`,
          okLabel: 'Excluir evento',
          onOk: () => actions.deleteEvent(event, 'one'),
        });
      },
      confirm: setConfirm,
    }),
    [today, ministries, events, actions]
  );

  return (
    <OverlayContext.Provider value={api}>
      {children}
      {opened ? (
        <EventSheet
          key={`${opened.eventId}-${opened.tab}`}
          eventId={opened.eventId}
          initialTab={opened.tab}
          onClose={() => setOpened(null)}
        />
      ) : null}
      {audience ? (
        <AudienceDialog
          key={audience.eventId}
          eventId={audience.eventId}
          pick={audience.pick}
          onClose={() => setAudience(null)}
        />
      ) : null}
      {picker ? <MemberPicker request={picker} onClose={() => setPicker(null)} /> : null}
      {share ? (
        <ShareDialog eventIds={share.eventIds} scope={share.scope} onClose={() => setShare(null)} />
      ) : null}
      {calendar ? <CalendarSyncDialog onClose={() => setCalendar(false)} /> : null}
      {install ? <InstallDialog onClose={() => setInstall(false)} /> : null}
      {privacy ? <PrivacyCenterDialog onClose={() => setPrivacy(false)} /> : null}
      {draft ? (
        <EventFormDialog
          initial={draft.initial}
          occurrenceOf={draft.occurrenceOf ?? null}
          copiedFrom={draft.copiedFrom ?? null}
          onClose={() => setDraft(null)}
        />
      ) : null}
      {deleting ? (
        <DeleteEventDialog
          event={deleting.event}
          initial={deleting.initial}
          onClose={() => setDeleting(null)}
        />
      ) : null}
      <ConfirmDialog options={confirm} onClose={() => setConfirm(null)} />
    </OverlayContext.Provider>
  );
}
